import { createRegexFromFormat } from './filenameFormat'

interface ParsedFilename {
  numericGroups: string[]
  separators: string[]
  nameSeparator: string
}

/**
 * Tokenizes a filename into grade, numeric groups, separators, and name separator.
 * Uses the known grade string as anchor, then scans for alternating separator + digit-group pairs.
 * @param fileName - The filename to parse
 * @param gradeString - The grade string to use as the leading anchor (e.g. "3", "K")
 * @returns Parsed structure or null if the filename doesn't match
 */
export function parseFilenameStructure(
  fileName: string,
  gradeString: string,
): ParsedFilename | null {
  const gradeStr = String(gradeString)
  if (!fileName.startsWith(gradeStr)) return null

  let rest = fileName.slice(gradeStr.length)
  const numericGroups: string[] = []
  const separators: string[] = []

  // Extract alternating separator + digit-group pairs
  const pairRegex = /^([^a-zA-Z\d]+)(\d+)/
  let match = rest.match(pairRegex)
  while (match) {
    separators.push(match[1])
    numericGroups.push(match[2])
    rest = rest.slice(match[0].length)
    match = rest.match(pairRegex)
  }

  if (numericGroups.length === 0) return null

  // Everything remaining is [nameSeparator][name]
  // Split at the first alphabetic character
  const namePartMatch = rest.match(/^([^a-zA-Z]*)([a-zA-Z].*)$/)
  const nameSeparator = namePartMatch ? namePartMatch[1] : ''

  return { numericGroups, separators, nameSeparator }
}

/**
 * Maps a numeric group count to format placeholder names.
 * @param groupCount - Number of numeric groups found in the filename
 * @returns Array of placeholder names (e.g. ['unit', 'lesson'] for 2 groups)
 */
export function assignPlaceholders(groupCount: number): string[] {
  switch (groupCount) {
    case 1:
      return ['lesson']
    case 2:
      return ['unit', 'lesson']
    case 3:
      return ['module', 'unit', 'lesson']
    case 4:
      return ['module', 'unit', 'lesson', 'character']
    default:
      return Array.from({ length: groupCount }, (_, i) => `group${i + 1}`)
  }
}

function majority<T>(items: T[]): T | undefined {
  const counts = new Map<T, number>()
  for (const item of items) {
    counts.set(item, (counts.get(item) ?? 0) + 1)
  }
  let best: T | undefined
  let bestCount = 0
  for (const [item, count] of counts) {
    if (count > bestCount) {
      best = item
      bestCount = count
    }
  }
  return best
}

/**
 * Analyzes an array of filenames to auto-detect the format string for matching files to tree nodes.
 * Uses majority voting across all files to determine separators and group count.
 * @param fileNames - Array of Google Drive filenames to analyze
 * @param gradeString - The grade identifier (e.g. "3", "K")
 * @returns Detected format string with match stats, or null if detection fails
 */
export function detectFilenameFormat(
  fileNames: string[],
  gradeString: string | number,
): { format: string; matchCount: number; totalFiles: number } | null {
  if (fileNames.length === 0) return null

  const gradeStr = String(gradeString)
  const parsed = fileNames
    .map((name) => parseFilenameStructure(name, gradeStr))
    .filter((p): p is ParsedFilename => p !== null)

  if (parsed.length === 0) return null

  // Find most common group count
  const groupCount = majority(parsed.map((p) => p.numericGroups.length))
  if (groupCount === undefined) return null

  // Filter to entries with the majority group count
  const matching = parsed.filter((p) => p.numericGroups.length === groupCount)

  // Determine separator at each position by majority vote
  const seps: string[] = []
  for (let i = 0; i < groupCount; i++) {
    const sepAtPos = matching.map((p) => p.separators[i])
    seps.push(majority(sepAtPos) ?? '.')
  }

  // Determine name separator by majority vote
  const nameSep = majority(matching.map((p) => p.nameSeparator)) ?? ' '

  // Build format string
  const placeholders = assignPlaceholders(groupCount)
  let format = '{grade}'
  for (let i = 0; i < groupCount; i++) {
    format += seps[i] + `{${placeholders[i]}}`
  }
  format += nameSep + '{name}'

  // Validate against all original filenames
  const regex = createRegexFromFormat(format)
  const matchCount = fileNames.filter((name) => regex.test(name)).length

  return { format, matchCount, totalFiles: fileNames.length }
}
